import { useEffect, useRef, useState } from 'react';
import { useGameSocket } from '../hooks/useGameSocket';
import { useGameStore } from '../store/gameStore';
import { useSound } from '../contexts/SoundContext';
import { Icon } from '../components/Icon';
import { SubjectSelector } from '../components/admin/SubjectSelector';
import { TimerDisplay } from '../components/game/TimerDisplay';
import type { TeamId } from '../types/game';

interface Props { gameId: string }

const teamBg: Record<TeamId, string> = { A: '#3b82f6', B: '#ef4444', C: '#10b981' };
const teamNames: Record<TeamId, string> = { A: 'EQUIPO A', B: 'EQUIPO B', C: 'EQUIPO C' };
const ALL_TEAMS: TeamId[] = ['A', 'B', 'C'];

// ─── Slot-machine roulette ────────────────────────────────────────────────────

function RouletteOrderDisplay({ teamOrder }: { teamOrder: TeamId[] }) {
  const [slots, setSlots] = useState<TeamId[]>(['A', 'A', 'A']);
  const [locked, setLocked] = useState<boolean[]>([false, false, false]);
  const [flash, setFlash] = useState<boolean[]>([false, false, false]);
  const [done, setDone] = useState(false);
  const intervals = useRef<(ReturnType<typeof setInterval> | null)[]>([null, null, null]);

  useEffect(() => {
    if (!teamOrder.length) return;

    setSlots(['A', 'A', 'A']);
    setLocked([false, false, false]);
    setFlash([false, false, false]);
    setDone(false);

    // Spin all slots
    for (let i = 0; i < teamOrder.length; i++) {
      intervals.current[i] = setInterval(() => {
        setSlots(prev => {
          const next = [...prev] as TeamId[];
          const choices = ALL_TEAMS.filter(t => t !== next[i]);
          next[i] = choices[Math.floor(Math.random() * choices.length)];
          return next;
        });
      }, 75);
    }

    // Lock each slot sequentially
    const lockAt = [1300, 2200, 3100];
    lockAt.slice(0, teamOrder.length).forEach((delay, idx) => {
      setTimeout(() => {
        if (intervals.current[idx]) {
          clearInterval(intervals.current[idx]!);
          intervals.current[idx] = null;
        }
        setSlots(prev => {
          const next = [...prev] as TeamId[];
          next[idx] = teamOrder[idx];
          return next;
        });
        setLocked(prev => { const n = [...prev]; n[idx] = true; return n; });
        setFlash(prev => { const n = [...prev]; n[idx] = true; return n; });
        setTimeout(() => setFlash(prev => { const n = [...prev]; n[idx] = false; return n; }), 400);
        if (idx === teamOrder.length - 1) setTimeout(() => setDone(true), 600);
      }, delay);
    });

    return () => { intervals.current.forEach(iv => { if (iv) clearInterval(iv); }); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(teamOrder)]);

  return (
    <div className="flex flex-col items-center select-none">
      <img 
                src="/SecLogo.png" 
                alt="Escuela Logo" 
                className="w-48 h-48 md:w-64 md:h-64 relative z-10 pb-4"
                style={{ filter: 'drop-shadow(6px 6px 0px var(--color-primary)) drop-shadow(0 0 20px rgba(152, 16, 250, 0.4))' }}
              />
      <div
        className="text-xs font-black uppercase mb-10"
        style={{ color: 'rgba(255,255,255,0.3)', letterSpacing: '0.35em' }}
      >
        Orden de equipos
      </div>
      {/* Slots row */}
      <div className="flex items-start gap-6">
        {teamOrder.map((_, slotIdx) => {
          const team = slots[slotIdx];
          const isLocked = locked[slotIdx];
          const isFlash = flash[slotIdx];
          const color = teamBg[team];

          return (
            <div key={slotIdx} className="flex flex-col items-center gap-3">

              {/* Position badge */}
              <div
                className="text-xs font-black uppercase"
                style={{
                  letterSpacing: '0.2em',
                  color: isLocked ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)',
                  transition: 'color 0.4s ease',
                }}
              >
                {slotIdx + 1}°
              </div>

              {/* Card */}
              <div
                style={{
                  position: 'relative',
                  width: 120,
                  height: 120,
                  borderRadius: '1rem',
                  overflow: 'hidden',
                  border: isLocked ? `4px solid ${color}` : '2px solid rgba(255,255,255,0.2)',
                  boxShadow: isFlash
                    ? `8px 8px 0px ${color}`
                    : isLocked
                      ? `6px 6px 0px ${color}`
                      : 'none',
                  transform: isFlash ? 'translate(-4px, -4px)' : isLocked ? 'translate(-2px, -2px)' : 'translate(0)',
                  transition: 'all 0.1s cubic-bezier(0.87, 0, 0.13, 1)',
                  background: 'var(--color-bg-surface)'
                }}
              >
                {/* Background fill */}
                <div
                  style={{
                    position: 'absolute', inset: 0,
                    background: isLocked ? color : 'transparent',
                    transition: 'background 0.1s ease',
                  }}
                />

                {/* Scan-lines (spinning only) */}
                {!isLocked && (
                  <div
                    style={{
                      position: 'absolute', inset: 0, zIndex: 1,
                      backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.18) 0px, rgba(0,0,0,0.18) 1px, transparent 1px, transparent 4px)',
                    }}
                  />
                )}

                {/* Top/bottom fade (spinning only) */}
                {!isLocked && (
                  <div
                    style={{
                      position: 'absolute', inset: 0, zIndex: 2,
                      background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 38%, transparent 62%, rgba(0,0,0,0.55) 100%)',
                    }}
                  />
                )}

                {/* Content */}
                <div
                  style={{
                    position: 'absolute', inset: 0, zIndex: 3,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 3,
                    filter: isLocked ? 'none' : 'blur(0.8px)',
                    transition: 'filter 0.2s ease',
                  }}
                >
                  <span style={{ fontSize: 44, fontWeight: 900, color: '#fff', lineHeight: 1, textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}>
                    {team}
                  </span>
                  {isLocked && (
                    <span
                      style={{
                        fontSize: 9, fontWeight: 800, letterSpacing: '0.14em',
                        color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase',
                        animation: 'fadeUp 0.4s ease forwards',
                      }}
                    >
                      {teamNames[team]}
                    </span>
                  )}
                </div>

                {/* Check mark */}
                {isLocked && (
                  <div
                    style={{
                      position: 'absolute', bottom: 8, right: 9, zIndex: 4,
                      fontSize: 13, color: 'rgba(255,255,255,0.65)',
                      animation: 'fadeIn 0.3s ease forwards',
                    }}
                  >
                    ✓
                  </div>
                )}
              </div>

              {/* Indicator dots */}
              <div style={{ display: 'flex', gap: 4 }}>
                {[0, 1, 2].map(d => (
                  <div
                    key={d}
                    style={{
                      width: 5, height: 5, borderRadius: '50%',
                      background: isLocked ? color : 'rgba(255,255,255,0.2)',
                      transition: 'background 0.4s ease',
                      animation: isLocked ? 'none' : `dotPulse 0.55s ${d * 0.1}s ease-in-out infinite alternate`,
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Final order summary */}
      {done && (
        <div
          style={{
            marginTop: 36,
            display: 'flex', alignItems: 'center', gap: 8,
            animation: 'fadeUp 0.6s cubic-bezier(0.34,1.56,0.64,1) forwards',
          }}
        >
          {teamOrder.map((team, i) => (
            <div key={team} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {i > 0 && <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 14, fontWeight: 700 }}>→</span>}
              <span style={{ fontSize: 13, fontWeight: 800, color: teamBg[team], letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {teamNames[team]}
              </span>
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes dotPulse {
          from { opacity: 0.2; transform: scale(0.75); }
          to   { opacity: 1;   transform: scale(1.25); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 0.65; }
        }
      `}</style>
    </div>
  );
}

// ─── ProjectorView ────────────────────────────────────────────────────────────

export function ProjectorView({ gameId }: Props) {
  useGameSocket(gameId, null, 'projector');
  const { game } = useGameStore();
  const sound = useSound();
  const prevWinner = useRef<TeamId | null>(null);
  const [winnerAlert, setWinnerAlert] = useState(false);
  const [stealAlert, setStealAlert] = useState(false);
  const [incorrectAlert, setIncorrectAlert] = useState(false);
  const [incorrectTeam, setIncorrectTeam] = useState<TeamId | null>(null);
  const [confetti, setConfetti] = useState(false);
  const [countdownExiting, setCountdownExiting] = useState(false);
  const countdownExitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentTurnTeam = game.teamOrder?.[game.currentTurnIndex ?? 0] as TeamId | undefined;

  // El equipo que realmente tiene la palabra (ganó el buzzer o es su turno)
  let activeTeam: TeamId | undefined = game.winner?.team || currentTurnTeam;
  
  // Si estamos en robo y nadie ha ganado el buzzer aún, mostramos quién podría robar (opcional)
  // Pero si alguien ganó el buzzer, ese es el activeTeam absoluto.
  let isStealing = game.roundPhase === 'steal';
  if (isStealing && game.winner?.team) {
    activeTeam = game.winner.team;
  }
  
  const isRoundEnd = game.roundPhase === 'round_end' && game.status === 'round_end';
  const showAnswer = isRoundEnd;

  useEffect(() => {
    if (game.winner?.team && game.winner.team !== prevWinner.current) {
      sound.play('buzz-won');
      setWinnerAlert(true);
      setConfetti(true);
      setTimeout(() => setWinnerAlert(false), 2500);
      setTimeout(() => setConfetti(false), 3000);
      prevWinner.current = game.winner.team;
    }
    if (!game.winner) prevWinner.current = null;
  }, [game.winner, sound]);

  useEffect(() => {
    if (game.roundPhase === 'steal') {
      sound.play('steal');
      setStealAlert(true);
      setTimeout(() => setStealAlert(false), 3000);
    }
  }, [game.roundPhase, sound]);

  useEffect(() => {
    if (game.status === 'incorrect_answer' && game.winner?.team) {
      sound.play('incorrect');
      setIncorrectTeam(game.winner.team);
      setIncorrectAlert(true);
      const timer = setTimeout(() => setIncorrectAlert(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [game.status, game.winner?.team, sound]);

  // ── Idle / Roulette screen ──────────────────────────────────────────────────
  if (game.status === 'idle' || game.roundPhase === 'roulette') {
    return (
      <div
        className="h-screen w-screen flex flex-col items-center justify-center relative overflow-hidden bg-bg-deep bg-grid-tech"
      >
        {game.roundPhase === 'roulette' && game.teamOrder.length > 0 && (
          <RouletteOrderDisplay teamOrder={game.teamOrder as TeamId[]} />
        )}

        {game.status === 'idle' && (
          <div className="flex flex-col items-center justify-center" style={{ animation: 'fadeUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}>
            <div className="relative mb-12">
              <div className="absolute inset-0 bg-primary blur-3xl opacity-30 rounded-full animate-pulse" />
              <img 
                src="/SecLogo.png" 
                alt="Escuela Logo" 
                className="w-48 h-48 md:w-64 md:h-64 relative z-10"
                style={{ filter: 'drop-shadow(6px 6px 0px var(--color-primary)) drop-shadow(0 0 20px rgba(152, 16, 250, 0.4))' }}
              />
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-2 uppercase tracking-widest text-center" style={{ textShadow: '4px 4px 0px var(--color-primary)' }}>
              Concurso Académico
            </h1>
            <h2 className="text-2xl md:text-3xl font-bold text-white/60 mb-10 uppercase tracking-[0.3em]">
              {game.grade}° GRADO
            </h2>
            
            <div className="flex gap-6 mb-12">
              {(['A', 'B', 'C'] as TeamId[]).map(t => (
                <div key={t} className="flex flex-col items-center gap-2">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-black shadow-[4px_4px_0_rgba(0,0,0,0.5)]" style={{ background: teamBg[t] }}>
                    {t}
                  </div>
                  <span className="text-xs font-bold text-white/40 uppercase tracking-widest">{teamNames[t]}</span>
                </div>
              ))}
            </div>

            <div className="px-8 py-4 rounded-xl border-2 border-white/20 bg-white/5 backdrop-blur-sm shadow-[4px_4px_0_var(--color-primary)]">
              <p className="text-lg md:text-xl font-bold text-white/80 uppercase tracking-widest flex items-center gap-4">
                <span className="w-4 h-4 rounded-full bg-primary animate-pulse shadow-[0_0_10px_var(--color-primary)]" />
                Esperando al moderador
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="h-screen w-screen flex flex-col overflow-hidden relative bg-bg-deep bg-grid-tech"
    >
      {confetti && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {Array.from({ length: 60 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10px',
                background: ['#f59e0b', '#3b82f6', '#ef4444', '#10b981', '#a855f7'][i % 5],
                animation: `confettiFall ${1.5 + Math.random()}s ease-in forwards`,
                animationDelay: `${Math.random() * 0.5}s`,
              }}
            />
          ))}
        </div>
      )}

      <header className="flex items-center justify-between px-8 py-4 flex-shrink-0">
        <div className="flex items-center gap-4">
          <Icon name="trophy" size="2xl" />
          <div className="flex flex-col">
            <span className="text-white/80 text-sm font-black uppercase tracking-wider">Ronda {Math.max(1, game.round)}</span>
            <span className="text-white/40 text-[10px] font-bold uppercase tracking-widest">{game.grade}° GRADO</span>
          </div>
        </div>
        {game.currentSubject && (
          <div
            className="px-5 py-1.5 text-white text-sm font-bold uppercase tracking-wider"
            style={{ border: `2px solid ${game.currentSubject.color}`, boxShadow: `2px 2px 0px ${game.currentSubject.color}` }}
          >
            {game.currentSubject.name}
          </div>
        )}
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {(['A', 'B', 'C'] as TeamId[]).map(t => (
              <div key={t} className="w-6 h-6 rounded-md flex items-center justify-center text-white text-[10px] font-black" style={{ background: teamBg[t] }}>
                {t}
              </div>
            ))}
          </div>
          <div className="text-white/40 text-xs font-bold uppercase tracking-widest">Concurso Académico</div>
        </div>
      </header>

      {winnerAlert && game.winner && (
        <div className="absolute inset-0 flex items-center justify-center z-40 pointer-events-none">
          <div
            className="relative text-center"
            style={{ animation: 'winnerPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}
          >
            <div className="absolute inset-0 rounded-3xl blur-3xl opacity-60"
              style={{ background: teamBg[game.winner.team] }} />
            <div
              className="relative px-16 py-10 rounded-3xl border-4"
              style={{
                background: `${teamBg[game.winner.team]}22`,
                borderColor: teamBg[game.winner.team],
                boxShadow: `0 0 80px ${teamBg[game.winner.team]}88`,
              }}
            >
              <Icon name="star" size="5xl" className="mb-2" />
              <div className="text-4xl font-black text-white">{teamNames[game.winner.team]}</div>
            </div>
          </div>
        </div>
      )}

      {stealAlert && !winnerAlert && (
        <div className="absolute inset-0 flex items-center justify-center z-40 pointer-events-none">
          <div
            className="relative text-center"
            style={{ animation: 'stealIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}
          >
            <div className="absolute inset-0 rounded-3xl blur-2xl opacity-50"
              style={{ background: '#f59e0b' }} />
            <div
              className="relative px-16 py-10 rounded-3xl border-4"
              style={{
                background: 'rgba(245, 158, 11, 0.75)',
                borderColor: '#f59e0b',
                boxShadow: '0 0 80px rgba(245, 158, 11, 0.5)',
              }}
            >
              <div className="text-5xl mb-2">{game.isSuddenDeath ? '🔥' : '↩'}</div>
              <div className="text-3xl font-black text-white">{game.isSuddenDeath ? '¡MUERTE SÚBITA!' : '¡MODO ROBO!'}</div>
            </div>
          </div>
        </div>
      )}

      {incorrectAlert && incorrectTeam && (
        <div className="absolute inset-0 flex items-center justify-center z-40 pointer-events-none">
          <div
            className="relative text-center"
            style={{ animation: 'stealIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}
          >
            <div className="absolute inset-0 rounded-3xl blur-2xl opacity-50"
              style={{ background: '#ef4444' }} />
            <div
              className="relative px-16 py-10 rounded-3xl border-4"
              style={{
                background: 'rgba(239, 68, 68, 0.75)',
                borderColor: '#ef4444',
                boxShadow: '0 0 80px rgba(239, 68, 68, 0.5)',
              }}
            >
              <div className="text-5xl mb-2">❌</div>
              <div className="text-3xl font-black text-white">¡INCORRECTO!</div>
              <div className="text-xl font-bold text-white/90 mt-2 uppercase tracking-wide">
                {teamNames[incorrectTeam]}
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 flex flex-col items-center justify-center px-8 pb-32 gap-6 relative z-10">
        {(currentTurnTeam || game.isSuddenDeath || game.status === 'incorrect_answer') && !winnerAlert && !isRoundEnd && (() => {
          if (game.status === 'incorrect_answer' && game.winner) {
            const incorrectTeam = game.winner.team;
            const color = teamBg[incorrectTeam];
            return (
              <div style={{ animation: 'turnIn 0.5s ease-out forwards' }} className="flex flex-col items-center gap-1">
                <div className="text-danger text-sm font-black uppercase tracking-widest mb-1 animate-pulse">❌ INCORRECTO</div>
                <div
                  className="px-8 py-3 rounded-2xl flex items-center gap-4 font-black text-white text-2xl"
                  style={{
                    background: '#ef444422',
                    border: `3px solid #ef4444`,
                    boxShadow: `0 0 30px rgba(239,68,68,0.5), 4px 4px 0 #ef4444`,
                  }}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-lg" style={{ background: color }}>
                    {incorrectTeam}
                  </div>
                  <span style={{ color }}>{teamNames[incorrectTeam]}</span>
                </div>
              </div>
            );
          }

          if (game.isSuddenDeath) {
            const speaker = game.winner?.team;
            if (speaker) {
              const color = teamBg[speaker];
              return (
                <div style={{ animation: 'turnIn 0.5s ease-out forwards' }} className="flex flex-col items-center gap-1">
                  <div className="text-white/40 text-xs font-black uppercase tracking-widest mb-1">🔥 RESPONDIENDO</div>
                  <div
                    className="px-8 py-3 rounded-2xl flex items-center gap-4 font-black text-white text-2xl"
                    style={{
                      background: `${color}22`,
                      border: `3px solid ${color}`,
                      boxShadow: `0 0 30px ${color}55, 4px 4px 0 ${color}`,
                    }}
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-lg" style={{ background: color }}>
                      {speaker}
                    </div>
                    <span style={{ color }}>{teamNames[speaker]}</span>
                  </div>
                </div>
              );
            }
            return (
                <div style={{ animation: 'turnIn 0.5s ease-out forwards' }} className="flex flex-col items-center gap-1 mb-4">
                  <div className="px-8 py-3 rounded-2xl font-black text-white text-3xl tracking-widest uppercase"
                       style={{ background: '#ef444422', border: '3px solid #ef4444', boxShadow: '0 0 40px rgba(239,68,68,0.5), 4px 4px 0 #ef4444' }}>
                    🔥 Muerte Súbita 🔥
                  </div>
                </div>
            );
          }

          // If in steal phase, we ONLY show the banner if someone has actually buzzed (game.winner)
          const stealer = game.roundPhase === 'steal' ? game.winner?.team : null;
          const isSteal = !!stealer;
          
          // The current speaker is the stealer or the original turn taker
          const speaker = (stealer || currentTurnTeam) as TeamId;
          const color = teamBg[speaker];
          
          // In steal phase, if nobody buzzed yet, we don't show THIS banner (the overlay 'MODO ROBO' takes care of it)
          if ((game.roundPhase === 'steal' && !stealer) || !speaker) return null;

          return (
            <div style={{ animation: 'turnIn 0.5s ease-out forwards' }} className="flex flex-col items-center gap-1">
              {isSteal ? (
                // Steal banner: show stealer clearly as the agent
                <>
                  <div className="text-white/40 text-xs font-black uppercase tracking-widest mb-1">⚡ ROBO</div>
                  <div
                    className="px-8 py-3 rounded-2xl flex items-center gap-4 font-black text-white text-2xl"
                    style={{
                      background: `${color}22`,
                      border: `3px solid ${color}`,
                      boxShadow: `0 0 30px ${color}55, 4px 4px 0 ${color}`,
                    }}
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-lg" style={{ background: color }}>
                      {speaker}
                    </div>
                    <div className="flex flex-col leading-tight">
                      <span style={{ color }}>{teamNames[speaker]}</span>
                      <span className="text-white/40 text-xs font-semibold normal-case tracking-wide">
                        roba el turno de {currentTurnTeam ? teamNames[currentTurnTeam] : 'otro equipo'}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                // Normal turn banner
                <>
                  <div className="text-white/40 text-xs font-black uppercase tracking-widest mb-1">🎯 TURNO DE</div>
                  <div
                    className="px-8 py-3 rounded-2xl flex items-center gap-4 font-black text-white text-2xl"
                    style={{
                      background: `${color}22`,
                      border: `3px solid ${color}`,
                      boxShadow: `0 0 30px ${color}55, 4px 4px 0 ${color}`,
                    }}
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-lg" style={{ background: color }}>
                      {speaker}
                    </div>
                    <span style={{ color }}>{teamNames[speaker]}</span>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {game.currentQuestion && (
          <div className="w-full max-w-4xl flex flex-col gap-6">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-black text-white leading-tight">
                {game.currentQuestion.question_text}
              </h1>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {game.currentQuestion.options.slice(0, 4).map((option, index) => {
                const isCorrect = showAnswer && option === game.currentQuestion?.answer;
                const letters = ['A', 'B', 'C', 'D'];
                return (
                  <div
                    key={option}
                    className={`
                      flex items-center gap-4 px-6 py-5 border-2 rounded-2xl font-bold
                      transition-all duration-100 ease-out
                      ${isCorrect
                        ? 'border-black bg-success text-black translate-x-[-4px] translate-y-[-4px]'
                        : 'border-white bg-bg-surface text-white'}
                    `}
                    style={{
                      boxShadow: isCorrect ? '8px 8px 0px rgba(255,255,255,1)' : '4px 4px 0px rgba(255,255,255,0.2)',
                    }}
                  >
                    <div
                      className="w-12 h-12 border-2 rounded-xl border-current flex items-center justify-center font-black text-current text-lg flex-shrink-0"
                      style={{ fontFamily: 'var(--font-family-mono)' }}
                    >
                      {letters[index]}
                    </div>
                    <span className="flex-1 text-current text-xl">{option}</span>
                    {isCorrect && <span className="text-black text-3xl font-black">✓</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!game.currentQuestion && !winnerAlert && (
          <div className="w-full max-w-5xl flex flex-col items-center gap-10" style={{ animation: 'fadeUp 0.6s ease-out forwards' }}>
            <h2 className="text-2xl font-black text-white/50 uppercase tracking-widest text-center flex items-center gap-4">
              <Icon name="target" /> CATEGORÍAS DISPONIBLES
            </h2>
            <div className="pointer-events-none scale-110 origin-top mt-4">
              <SubjectSelector
                subjects={game.subjects}
                selectedId={null}
                usedSubjects={game.usedSubjects}
                onSelect={() => {}}
              />
            </div>
          </div>
        )}
      </main>

      <footer className="absolute bottom-0 left-0 w-full p-8 pointer-events-none">
        <div className="flex flex-col gap-2 items-start pointer-events-auto relative z-10">
          {(['A', 'B', 'C'] as TeamId[]).map(t => (
            <div key={t} className="relative px-4 py-2 rounded-lg shadow-[2px_2px_0_rgba(0,0,0,0.5)]" style={{ background: `${teamBg[t]}22` }}>
              <div className="absolute inset-0 rounded-lg border" style={{ borderColor: `${teamBg[t]}44` }} />
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-black"
                  style={{ background: teamBg[t] }}>
                  {t}
                </div>
                <span className="text-white font-black text-xl tabular-nums leading-none">{game.scores[t]}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center flex flex-col items-center pointer-events-auto z-0">
          {game.timerExpiresAt && (
            <TimerDisplay 
              expiresAt={game.timerExpiresAt} 
              variant="bar"
              className="w-[50vw] min-w-[500px] max-w-[800px]"
            />
          )}
        </div>
      </footer>

      {/* Countdown overlay — shows only during 'question_countdown' phase */}
      {(game.status === 'question_countdown' || countdownExiting) && game.timerExpiresAt && (() => {
        const expired = game.timerExpiresAt <= Date.now();
        // Trigger exit animation once locally
        if (expired && !countdownExiting) {
          setCountdownExiting(true);
          if (countdownExitTimer.current) clearTimeout(countdownExitTimer.current);
          countdownExitTimer.current = setTimeout(() => setCountdownExiting(false), 300);
        }
        return (
          <div
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center pointer-events-auto"
            style={{
              background: 'var(--color-bg-deep)',
              animation: expired ? 'countdownExit 0.3s cubic-bezier(0.4,0,1,1) forwards' : 'none',
            }}
          >
            <h2 className="text-3xl md:text-4xl text-white/40 font-black uppercase tracking-widest mb-6">
              La pregunta inicia en...
            </h2>
            {!expired && <TimerDisplay expiresAt={game.timerExpiresAt} variant="giant" />}
          </div>
        );
      })()}

      <style>{`
        @keyframes countdownExit {
          0%   { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.1); }
        }
        @keyframes numberPop {
          0%   { transform: scale(0.6); opacity: 0; }
          40%  { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes confettiFall {
          0%   { transform: translateY(0) rotate(0deg);    opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes winnerPop {
          0%   { transform: scale(0.5); opacity: 0; }
          60%  { transform: scale(1.1); }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes stealIn {
          0%   { transform: scale(0.5); opacity: 0; }
          60%  { transform: scale(1.1); }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes turnIn {
          0%   { transform: translateY(-20px); opacity: 0; }
          100% { transform: translateY(0);     opacity: 1; }
        }
      `}</style>
    </div>
  );
}